import React, { useState } from "react";
import { useParams } from "react-router-dom";
import MainCalculatorsList from "../MainCalculators";
import SharedHelmet from "../../Helmet";
import RelatedLinks from "../../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../../Ads";
function VitaminDoseCalculator() {
  const { vitamin } = useParams();
  const [age, setAge] = useState("");
  const [sex, setSex] = useState("male");
  const [isPregnant, setIsPregnant] = useState(false);
  const [isLactating, setIsLactating] = useState(false);
  const [dose, setDose] = useState(null);
  const [error, setError] = useState("");
  // Vitamin names mapping
  const vitaminNames = {
    a: "فيتامين A",
    c: "فيتامين C",
    d: "فيتامين D",
    e: "فيتامين E",
    k: "فيتامين K",
    b1: "الثيامين (B1)",
    thiamin: "الثيامين (B1)",
    b2: "الريبوفلافين (B2)",
    riboflavin: "الريبوفلافين (B2)",
    b3: "النياسين (B3)",
    niacin: "النياسين (B3)",
    b5: "حمض البانتوتينيك (B5)",
    pantothenic_acid: "حمض البانتوتينيك (B5)",
    b6: "البيريدوكسين (B6)",
    pyridoxine: "البيريدوكسين (B6)",
    b7: "البيوتين (B7)",
    biotin: "البيوتين (B7)",
    b9: "حمض الفوليك (B9)",
    folate: "حمض الفوليك (B9)",
    b12: "الكوبالامين (B12)",
    caynocobalamin: "الكوبالامين (B12)",
    choline: "الكولين",
  };
  const vitaminUnits = {
    a: "ميكروغرام",
    c: "ملغ",
    d: "ميكروغرام",
    e: "ملغ",
    k: "ميكروغرام",
    b1: "ملغ",
    thiamin: "ملغ",
    b2: "ملغ",
    riboflavin: "ملغ",
    b3: "ملغ",
    niacin: "ملغ",
    b5: "ملغ",
    pantothenic_acid: "ملغ",
    b6: "ملغ",
    pyridoxine: "ملغ",
    b7: "ميكروغرام",
    biotin: "ميكروغرام",
    b9: "ميكروغرام",
    folate: "ميكروغرام",
    b12: "ميكروغرام",
    caynocobalamin: "ميكروغرام",
    choline: "ملغ",
  };
  const normalizedVitamin = vitamin.toLowerCase();
  const vitaminName = vitaminNames[normalizedVitamin] || vitamin;
  const vitaminUnit = vitaminUnits[normalizedVitamin] || "ميكروغرام";
  // Map URL vitamins to internal keys
  const vitaminKeyMap = {
    a: "A",
    c: "C",
    d: "D",
    e: "E",
    k: "K",
    b1: "B1",
    thiamin: "B1",
    b2: "B2",
    riboflavin: "B2",
    b3: "B3",
    niacin: "B3",
    b5: "B5",
    pantothenic_acid: "B5",
    b6: "B6",
    pyridoxine: "B6",
    b7: "B7",
    biotin: "B7",
    b9: "B9",
    folate: "B9",
    b12: "B12",
    caynocobalamin: "B12",
    choline: "Choline",
  };
  const vitaminKey = vitaminKeyMap[normalizedVitamin] || "A";
  // Get vitamin dose based on age, sex, pregnancy, and lactation
  const getVitaminDose = (vitKey, ageValue, gender, pregnant, lactating) => {
    const dosageTable = {
      A: {
        male: {
          "0-6m": 400,
          "7-12m": 500,
          "1-3y": 300,
          "4-8y": 400,
          "9-13y": 600,
          "14-18y": 900,
          "19-50y": 900,
          "51+y": 900,
        },
        female: {
          "0-6m": 400,
          "7-12m": 500,
          "1-3y": 300,
          "4-8y": 400,
          "9-13y": 600,
          "14-18y": 700,
          "19-50y": 700,
          "51+y": 700,
        },
        pregnant: 770,
        lactating: 1300,
      },
      C: {
        male: {
          "0-6m": 40,
          "7-12m": 50,
          "1-3y": 15,
          "4-8y": 25,
          "9-13y": 45,
          "14-18y": 75,
          "19-50y": 90,
          "51+y": 90,
        },
        female: {
          "0-6m": 40,
          "7-12m": 50,
          "1-3y": 15,
          "4-8y": 25,
          "9-13y": 45,
          "14-18y": 65,
          "19-50y": 75,
          "51+y": 75,
        },
        pregnant: 85,
        lactating: 120,
      },
      D: {
        male: {
          "0-6m": 10,
          "7-12m": 10,
          "1-3y": 15,
          "4-8y": 15,
          "9-13y": 15,
          "14-18y": 15,
          "19-50y": 15,
          "51-70y": 15,
          "71+y": 20,
        },
        female: {
          "0-6m": 10,
          "7-12m": 10,
          "1-3y": 15,
          "4-8y": 15,
          "9-13y": 15,
          "14-18y": 15,
          "19-50y": 15,
          "51-70y": 15,
          "71+y": 20,
        },
        pregnant: 15,
        lactating: 15,
      },
      E: {
        male: {
          "0-6m": 4,
          "7-12m": 5,
          "1-3y": 6,
          "4-8y": 7,
          "9-13y": 11,
          "14-18y": 15,
          "19-50y": 15,
          "51+y": 15,
        },
        female: {
          "0-6m": 4,
          "7-12m": 5,
          "1-3y": 6,
          "4-8y": 7,
          "9-13y": 11,
          "14-18y": 15,
          "19-50y": 15,
          "51+y": 15,
        },
        pregnant: 15,
        lactating: 19,
      },
      K: {
        male: {
          "0-6m": 2,
          "7-12m": 2.5,
          "1-3y": 30,
          "4-8y": 55,
          "9-13y": 60,
          "14-18y": 75,
          "19-50y": 120,
          "51+y": 120,
        },
        female: {
          "0-6m": 2,
          "7-12m": 2.5,
          "1-3y": 30,
          "4-8y": 55,
          "9-13y": 60,
          "14-18y": 75,
          "19-50y": 90,
          "51+y": 90,
        },
        pregnant: 90,
        lactating: 90,
      },
      B1: {
        male: {
          "0-6m": 0.2,
          "7-12m": 0.3,
          "1-3y": 0.5,
          "4-8y": 0.6,
          "9-13y": 0.9,
          "14-18y": 1.2,
          "19-50y": 1.2,
          "51+y": 1.2,
        },
        female: {
          "0-6m": 0.2,
          "7-12m": 0.3,
          "1-3y": 0.5,
          "4-8y": 0.6,
          "9-13y": 0.9,
          "14-18y": 1.0,
          "19-50y": 1.1,
          "51+y": 1.1,
        },
        pregnant: 1.4,
        lactating: 1.4,
      },
      B2: {
        male: {
          "0-6m": 0.3,
          "7-12m": 0.4,
          "1-3y": 0.5,
          "4-8y": 0.6,
          "9-13y": 0.9,
          "14-18y": 1.3,
          "19-50y": 1.3,
          "51+y": 1.3,
        },
        female: {
          "0-6m": 0.3,
          "7-12m": 0.4,
          "1-3y": 0.5,
          "4-8y": 0.6,
          "9-13y": 0.9,
          "14-18y": 1.0,
          "19-50y": 1.1,
          "51+y": 1.1,
        },
        pregnant: 1.4,
        lactating: 1.6,
      },
      B3: {
        male: {
          "0-6m": 2,
          "7-12m": 4,
          "1-3y": 6,
          "4-8y": 8,
          "9-13y": 12,
          "14-18y": 16,
          "19-50y": 16,
          "51+y": 16,
        },
        female: {
          "0-6m": 2,
          "7-12m": 4,
          "1-3y": 6,
          "4-8y": 8,
          "9-13y": 12,
          "14-18y": 14,
          "19-50y": 14,
          "51+y": 14,
        },
        pregnant: 18,
        lactating: 17,
      },
      B6: {
        male: {
          "0-6m": 0.1,
          "7-12m": 0.3,
          "1-3y": 0.5,
          "4-8y": 0.6,
          "9-13y": 1.0,
          "14-18y": 1.3,
          "19-50y": 1.3,
          "51-70y": 1.7,
          "71+y": 1.7,
        },
        female: {
          "0-6m": 0.1,
          "7-12m": 0.3,
          "1-3y": 0.5,
          "4-8y": 0.6,
          "9-13y": 1.0,
          "14-18y": 1.2,
          "19-50y": 1.3,
          "51-70y": 1.5,
          "71+y": 1.5,
        },
        pregnant: 1.9,
        lactating: 2.0,
      },
      B9: {
        male: {
          "0-6m": 65,
          "7-12m": 80,
          "1-3y": 150,
          "4-8y": 200,
          "9-13y": 300,
          "14-18y": 400,
          "19-50y": 400,
          "51+y": 400,
        },
        female: {
          "0-6m": 65,
          "7-12m": 80,
          "1-3y": 150,
          "4-8y": 200,
          "9-13y": 300,
          "14-18y": 400,
          "19-50y": 400,
          "51+y": 400,
        },
        pregnant: 600,
        lactating: 500,
      },
      B12: {
        male: {
          "0-6m": 0.4,
          "7-12m": 0.5,
          "1-3y": 0.9,
          "4-8y": 1.2,
          "9-13y": 1.8,
          "14-18y": 2.4,
          "19-50y": 2.4,
          "51+y": 2.4,
        },
        female: {
          "0-6m": 0.4,
          "7-12m": 0.5,
          "1-3y": 0.9,
          "4-8y": 1.2,
          "9-13y": 1.8,
          "14-18y": 2.4,
          "19-50y": 2.4,
          "51+y": 2.4,
        },
        pregnant: 2.6,
        lactating: 2.8,
      },
      B5: {
        male: {
          "0-6m": 1.7,
          "7-12m": 1.8,
          "1-3y": 2,
          "4-8y": 3,
          "9-13y": 4,
          "14-18y": 5,
          "19-50y": 5,
          "51+y": 5,
        },
        female: {
          "0-6m": 1.7,
          "7-12m": 1.8,
          "1-3y": 2,
          "4-8y": 3,
          "9-13y": 4,
          "14-18y": 5,
          "19-50y": 5,
          "51+y": 5,
        },
        pregnant: 6,
        lactating: 7,
      },
      B7: {
        male: {
          "0-6m": 5,
          "7-12m": 6,
          "1-3y": 8,
          "4-8y": 12,
          "9-13y": 20,
          "14-18y": 25,
          "19-50y": 30,
          "51+y": 30,
        },
        female: {
          "0-6m": 5,
          "7-12m": 6,
          "1-3y": 8,
          "4-8y": 12,
          "9-13y": 20,
          "14-18y": 25,
          "19-50y": 30,
          "51+y": 30,
        },
        pregnant: 30,
        lactating: 35,
      },
      Choline: {
        male: {
          "0-6m": 125,
          "7-12m": 150,
          "1-3y": 200,
          "4-8y": 250,
          "9-13y": 375,
          "14-18y": 550,
          "19-50y": 550,
          "51+y": 550,
        },
        female: {
          "0-6m": 125,
          "7-12m": 150,
          "1-3y": 200,
          "4-8y": 250,
          "9-13y": 375,
          "14-18y": 400,
          "19-50y": 425,
          "51+y": 425,
        },
        pregnant: 450,
        lactating: 550,
      },
    };
    // Determine age category
    let ageCategory;
    const ageNum = parseInt(ageValue);
    if (ageNum < 1) {
      if (ageNum < 0.5) ageCategory = "0-6m";
      else ageCategory = "7-12m";
    } else if (ageNum >= 1 && ageNum <= 3) {
      ageCategory = "1-3y";
    } else if (ageNum >= 4 && ageNum <= 8) {
      ageCategory = "4-8y";
    } else if (ageNum >= 9 && ageNum <= 13) {
      ageCategory = "9-13y";
    } else if (ageNum >= 14 && ageNum <= 18) {
      ageCategory = "14-18y";
    } else if (ageNum >= 19 && ageNum <= 50) {
      ageCategory = "19-50y";
    } else if (ageNum >= 51 && ageNum <= 70) {
      ageCategory = "51-70y" in dosageTable[vitKey][gender]
        ? "51-70y"
        : "51+y";
    } else {
      ageCategory = "71+y" in dosageTable[vitKey][gender] ? "71+y" : "51+y";
    }
    // Check for pregnancy or lactation
    if (gender === "female" && pregnant) {
      return dosageTable[vitKey].pregnant;
    }
    if (gender === "female" && lactating) {
      return dosageTable[vitKey].lactating;
    }
    return dosageTable[vitKey][gender][ageCategory];
  };
  const calculateDailyDose = () => {
    try {
      if (!age || age < 0 || age > 120) {
        setError("الرجاء إدخال عمر صحيح (0-120 سنة)");
        setDose(null);
        return;
      }
      const calculatedDose = getVitaminDose(
        vitaminKey,
        age,
        sex,
        isPregnant,
        isLactating
      );
      setDose(calculatedDose);
      setError("");
    } catch (error) {
      setDose(null);
      setError("حدث خطأ في حساب الجرعة. الرجاء التحقق من البيانات المدخلة.");
    }
  };
  const handleCalculate = (e) => {
    e.preventDefault();
    calculateDailyDose();
  };
  const handleReset = () => {
    setAge("");
    setSex("male");
    setIsPregnant(false);
    setIsLactating(false);
    setDose(null);
    setError("");
  };
  const TITLE = `حاسبة ${vitaminName} - احسب احتياجك اليومي من ${vitaminName}`;
  const DESCRIPTION = `احسب الجرعة اليومية المثلى من ${vitaminName} بناءً على العمر والجنس باستخدام حاسبتنا الدقيقة. حاسبة ${vitaminName} توفر لك الجرعة المناسبة للرجال والنساء والأطفال والحوامل والمرضعات لضمان صحة جيدة. احصل على التقدير الدقيق لاحتياجاتك اليومية من ${vitaminName}.`;
  const KEYWORDS = `الجرعة اليومية من ${vitaminName}, احتياج الجسم من ${vitaminName}, حاسبة ${vitaminName}, ${vitaminName} للحوامل, ${vitaminName} للأطفال, نقص ${vitaminName}, مصادر ${vitaminName}`;
  const OG_URL = `https://do-calculate.com/calculator/ar/vitamin_daily_dose/${vitamin}/`;
  // Generate Schema.org structured data
  const generateSchemaData = () => {
    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "الصفحة الرئيسية",
          item: "https://do-calculate.com/",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "الحاسبات",
          item: "https://do-calculate.com/ar/all-calculators/",
        },
        {
          "@type": "ListItem",
          position: 3,
          name: "حاسبة الفيتامينات",
          item: "https://do-calculate.com/calculator/ar/vitamin_daily_dose/",
        },
        {
          "@type": "ListItem",
          position: 4,
          name: `حاسبة ${vitaminName}`,
          item: OG_URL,
        },
      ],
    };
    const calculatorSchema = {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: `حاسبة ${vitaminName}`,
      description: DESCRIPTION,
      url: OG_URL,
      applicationCategory: "HealthApplication",
      operatingSystem: "Any",
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "USD",
      },
    };
    return [breadcrumbSchema, calculatorSchema];
  };
  // Vitamin-specific information
  const vitaminInfo = {
    a: {
      importance:
        "فيتامين A ضروري للرؤية الصحية، خاصةً الرؤية الليلية. يعزز مناعة الجسم ويساعد في الحفاظ على صحة الجلد والأغشية المخاطية. كما يلعب دوراً أساسياً في نمو الخلايا وتجديد الأنسجة، ويدعم صحة العظام والأسنان.",
      sources:
        "الجزر، البطاطا الحلوة، اليقطين، السبانخ، الكرنب، البروكلي، الكبد، البيض، الحليب، المانجو، المشمش، الشمام",
      deficiency:
        "جفاف العين، العمى الليلي، ضعف المناعة، جفاف الجلد، تأخر النمو عند الأطفال، زيادة خطر العدوى",
    },
    c: {
      importance:
        "فيتامين C هو مضاد أكسدة قوي يساعد في تقوية جهاز المناعة وحماية الخلايا من التلف. يساهم في إنتاج الكولاجين اللازم لصحة الجلد والأوعية الدموية والعظام. يساعد أيضاً في امتصاص الحديد من الأطعمة النباتية وتسريع التئام الجروح.",
      sources:
        "البرتقال، الليمون، الجريب فروت، الفراولة، الكيوي، الجوافة، الفلفل الحلو (الملون)، البروكلي، الطماطم، السبانخ",
      deficiency:
        "نزيف اللثة، بطء التئام الجروح، كدمات سهلة، جفاف الجلد، ضعف المناعة، آلام المفاصل، في الحالات الشديدة: داء الاسقربوط",
    },
    d: {
      importance:
        "فيتامين D يعزز امتصاص الكالسيوم والفوسفور في الجسم، مما يساعد في بناء والحفاظ على صحة العظام والأسنان. يلعب دوراً مهماً في تعزيز وظيفة الجهاز المناعي وتنظيم نمو الخلايا. كما يؤثر على المزاج وصحة القلب.",
      sources:
        "أشعة الشمس (10-30 دقيقة يومياً)، الأسماك الدهنية (سلمون، تونة، سردين)، صفار البيض، الحليب المدعم، عصير البرتقال المدعم، الفطر المعرض للأشعة فوق البنفسجية",
      deficiency:
        "ضعف العظام وآلامها، هشاشة العظام، الكساح عند الأطفال، ضعف العضلات، التعب المزمن، الاكتئاب، ضعف المناعة",
    },
    e: {
      importance:
        "فيتامين E هو مضاد أكسدة قوي يحمي الخلايا من التلف الناتج عن الجذور الحرة. يساهم في الحفاظ على صحة الجلد والعينين والشعر. يلعب دوراً في تعزيز نظام المناعة وحماية القلب والأوعية الدموية. يساعد أيضاً في توسيع الأوعية الدموية ومنع تجلط الدم.",
      sources:
        "المكسرات (لوز، بندق، فول سوداني)، البذور (عباد الشمس، اليقطين)، الزيوت النباتية (زيت الزيتون، زيت عباد الشمس)، السبانخ، البروكلي، الأفوكادو، الكيوي",
      deficiency:
        "ضعف العضلات، مشاكل في الرؤية، ضعف المناعة، خدر ووخز في الأطراف، صعوبة في المشي، في الحالات الشديدة: تلف الأعصاب",
    },
    k: {
      importance:
        "فيتامين K ضروري لتخثر الدم السليم ومنع النزيف الزائد. يلعب دوراً مهماً في صحة العظام من خلال تنظيم الكالسيوم وتعزيز كثافة العظام. يساهم أيضاً في صحة القلب والأوعية الدموية من خلال منع تكلس الشرايين.",
      sources:
        "الخضروات الورقية الخضراء (كرنب، سبانخ، خس، بروكلي، كرنب بروكسل)، البقدونس، الكزبرة، الزيوت النباتية، الكبد، البيض، الأفوكادو",
      deficiency:
        "سهولة النزيف وصعوبة توقفه، كدمات متكررة، نزيف اللثة، نزيف الأنف، نزيف حيض غزير، في الحالات الشديدة: نزيف داخلي",
    },
    b1: {
      importance:
        "الثيامين (B1) ضروري لتحويل الكربوهيدرات إلى طاقة. يدعم وظيفة الأعصاب والعضلات بشكل صحيح. يلعب دوراً في نمو وتطور ووظيفة الخلايا. ضروري لصحة القلب والجهاز الهضمي والعصبي.",
      sources:
        "الحبوب الكاملة، الخبز المدعم، المعكرونة، الأرز البني، لحم الخنزير، الكبد، البقوليات، المكسرات، البذور، السمك",
      deficiency:
        "التعب والضعف، فقدان الشهية، الارتباك وفقدان الذاكرة، تنميل في اليدين والقدمين، في الحالات الشديدة: البري بري (مرض عصبي)",
    },
    b2: {
      importance:
        "الريبوفلافين (B2) يساعد في تحويل الطعام إلى طاقة. يعزز صحة الجلد والعينين والأعصاب. يلعب دوراً في إنتاج خلايا الدم الحمراء. يساعد في عملية التمثيل الغذائي للفيتامينات الأخرى مثل B6 وحمض الفوليك.",
      sources:
        "الحليب ومنتجات الألبان، البيض، اللحوم الخالية من الدهون، الكبد، الخضروات الورقية الخضراء، الحبوب الكاملة المدعمة، المكسرات",
      deficiency:
        "التهاب الفم والشفتين، التهاب اللسان واحمراره، تشققات في زوايا الفم، التهاب الجلد، حساسية العين للضوء، فقر الدم",
    },
    b3: {
      importance:
        "النياسين (B3) يساهم في تحويل الطعام إلى طاقة. يدعم صحة الجلد والجهاز العصبي والجهاز الهضمي. يساعد في خفض مستويات الكولسترول الضار ورفع الكولسترول الجيد. يلعب دوراً في إنتاج الهرمونات الجنسية والتوتر.",
      sources:
        "اللحوم الحمراء، الدجاج، السمك، الكبد، الفول السوداني، البقوليات، الحبوب الكاملة المدعمة، الفطر، البطاطس",
      deficiency:
        "التعب والصداع، الإسهال، الطفح الجلدي، الارتباك وفقدان الذاكرة، في الحالات الشديدة: البلاغرا (مرض يسبب الخرف والإسهال والتهاب الجلد)",
    },
    b5: {
      importance:
        "حمض البانتوتينيك (B5) يساهم في تحويل الطعام إلى طاقة. يدعم صحة الجلد والشعر والعينين. يلعب دوراً في تصنيع الهرمونات والكولسترول. ضروري لإنتاج خلايا الدم الحمراء والناقلات العصبية.",
      sources:
        "اللحوم، الكبد، الدجاج، الأسماك، البيض، الحليب، الفطر، الأفوكادو، البطاطا الحلوة، البروكلي، البقوليات",
      deficiency:
        "نادر جداً، لكن قد يسبب: التعب، الأرق، الاكتئاب، التهيج، تنميل في القدمين، آلام المعدة",
    },
    b6: {
      importance:
        "البيريدوكسين (B6) يلعب دوراً في تحويل الطعام إلى طاقة. يدعم وظائف الدماغ والجهاز العصبي. يساهم في تصنيع الهيموغلوبين الذي ينقل الأكسجين في الدم. يساعد في تنظيم المزاج والنوم من خلال إنتاج السيروتونين والميلاتونين.",
      sources:
        "الدجاج، السمك، لحم البقر، الكبد، البطاطا، الموز، الحمص، الحبوب الكاملة المدعمة، السبانخ، المكسرات",
      deficiency:
        "فقر الدم، طفح جلدي، تشققات في الشفاه، التهاب اللسان، الاكتئاب والتهيج، ضعف المناعة، الارتباك",
    },
    b7: {
      importance:
        "البيوتين (B7) يساعد في تحويل الطعام إلى طاقة. يدعم صحة الجلد والشعر والأظافر. يلعب دوراً في تصنيع الأحماض الدهنية والجلوكوز. ضروري لنمو الخلايا وإنتاج الأحماض الأمينية.",
      sources:
        "صفار البيض، الكبد، المكسرات (خاصة اللوز)، البذور، السلمون، الأفوكادو، البطاطا الحلوة، السبانخ، البروكلي",
      deficiency:
        "تساقط الشعر، طفح جلدي حول العينين والأنف والفم، أظافر هشة، التعب، الاكتئاب، تنميل في الأطراف",
    },
    b9: {
      importance:
        "حمض الفوليك (B9) مهم جداً لتكوين خلايا الدم الحمراء ونمو الخلايا. ضروري بشكل خاص للنساء الحوامل لمنع تشوهات الأنبوب العصبي لدى الجنين. يدعم صحة الدماغ والجهاز العصبي. يساعد في إنتاج الحمض النووي (DNA) وإصلاحه.",
      sources:
        "الخضروات الورقية الخضراء (سبانخ، كرنب)، البقوليات (عدس، فاصوليا)، الحمضيات، الأفوكادو، البروكلي، الحبوب المدعمة، الكبد",
      deficiency:
        "فقر الدم الضخم الأرومات، التعب والضعف، تقرحات الفم واللسان، شيب الشعر المبكر، لدى الحوامل: خطر تشوهات الجنين",
    },
    b12: {
      importance:
        "الكوبالامين (B12) ضروري لتكوين خلايا الدم الحمراء وصحة الجهاز العصبي. يساعد في إنتاج الحمض النووي (DNA) وإصلاحه. يعزز من قدرة الجسم على استخدام الدهون والبروتينات. يلعب دوراً في الوقاية من فقر الدم الضخم الأرومات.",
      sources:
        "اللحوم، الكبد، الأسماك، البيض، الحليب ومنتجات الألبان، الحبوب المدعمة (للنباتيين)، الخميرة الغذائية المدعمة",
      deficiency:
        "فقر الدم الشديد، التعب والضعف، وخز وخدر في اليدين والقدمين، صعوبة في التوازن والمشي، مشاكل في الذاكرة والتركيز، التهاب اللسان",
    },
    choline: {
      importance:
        "الكولين مهم لصحة الدماغ والذاكرة ووظائف الكبد. يساعد في تصنيع الناقلات العصبية المهمة للذاكرة والمزاج. يلعب دوراً في نمو الخلايا وعملية التمثيل الغذائي. ضروري لنقل الدهون من الكبد ووظائف الجهاز العصبي.",
      sources:
        "البيض (خاصة الصفار)، الكبد، السلمون، الدجاج، اللحوم الحمراء، البروكلي، كرنب بروكسل، الفول السوداني، فول الصويا",
      deficiency:
        "تلف الكبد، أمراض الكبد الدهني، تلف العضلات، ضعف الذاكرة، مشاكل في النمو العصبي لدى الأطفال",
    },
  };
  const currentVitaminInfo =
    vitaminInfo[normalizedVitamin] || vitaminInfo["a"];
  return (
    <div className="rtl" style={{ direction: "rtl" }}>
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
        SCHEMA={generateSchemaData()}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div
            className="bg-success text-white text-center"
            style={{ padding: "15px", borderRadius: "12px" }}
          >
            <h1 style={{ fontSize: "22px", margin: 0 }}>{TITLE}</h1>
          </div>
          <div className="border border-light mt-3 p-3 bg-white shadow-sm">
            <div className="mb-4">
              <p style={{ fontSize: "16px", lineHeight: "1.8" }}>
                مرحباً بك في <strong>حاسبة {vitaminName}</strong>! باستخدام هذه
                الأداة الدقيقة، يمكنك حساب الجرعة اليومية المثلى من{" "}
                {vitaminName} بناءً على عمرك وجنسك وحالتك الصحية. سواء كنت
                رجلاً أو امرأة، طفلاً أو بالغاً، حاملاً أو مرضعة، فإن حاسبتنا
                توفر لك توصيات دقيقة لضمان حصولك على الكمية المثلى من{" "}
                {vitaminName}.
              </p>
            </div>
            <hr />
            <MobileUnderCalAds />
            <div className="row">
             <div className="col-sm-12 col-md-6 calculatorStyle">
                <div className="p-3 mb-3 bg-success text-white text-center rounded">
                  <h2 style={{ fontSize: "18px", margin: 0 }}>
                    احسب احتياجك من {vitaminName}
                  </h2>
                </div>
                <form onSubmit={handleCalculate}>
                  <div className="form-group mb-3">
                    <label  htmlFor="age">
                      <strong>العمر (بالسنوات):</strong>
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      id="age"
                      value={age}
                      onChange={(e) => setAge(e.target.value)}
                      placeholder="أدخل عمرك"
                      min="0"
                      max="120"
                      required
                    />
                  </div>
                  <div className="form-group mb-3">
                    <label  htmlFor="sex">
                      <strong>الجنس:</strong>
                    </label>
                    <select
                      className="form-control"
                      id="sex"
                      value={sex}
                      onChange={(e) => {
                        setSex(e.target.value);
                        if (e.target.value === "male") {
                          setIsPregnant(false);
                          setIsLactating(false);
                        }
                      }}
                    >
                      <option value="male">ذكر</option>
                      <option value="female">أنثى</option>
                    </select>
                  </div>
                  {sex === "female" && (
                    <>
                      <div className="form-group mb-3">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="pregnant"
                            checked={isPregnant}
                            onChange={(e) => {
                              setIsPregnant(e.target.checked);
                              if (e.target.checked) setIsLactating(false);
                            }}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="pregnant"
                          >
                            حامل
                          </label>
                        </div>
                      </div>
                      <div className="form-group mb-3">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="lactating"
                            checked={isLactating}
                            onChange={(e) => {
                              setIsLactating(e.target.checked);
                              if (e.target.checked) setIsPregnant(false);
                            }}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="lactating"
                          >
                            مرضعة
                          </label>
                        </div>
                      </div>
                    </>
                  )}
                  <hr />
                  <div className="d-flex gap-2">
                    <button
                      type="submit"
                      className="btn bg-success text-white"
                      style={{ fontSize: "16px", padding: "10px 30px" }}
                    >
                      احسب
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleReset}
                       style={{ fontSize: "16px", padding: "10px 30px", marginRight: "10px" }}
                    >
                      امسح

                    </button>
                  </div>
                </form>
              </div>
              <div className="col-sm-12 col-md-4">
                {error && (
                  <div className="alert alert-warning" role="alert">
                    <strong>خطأ:</strong> {error}
                  </div>
                )}
                {dose !== null && !error && (
                  <div className="calculatorStyle">
                    <div className="p-3 mb-3 bg-success text-white text-center rounded">
                      <h3 style={{ fontSize: "18px", margin: 0 }}>النتيجة</h3>
                    </div>
                    <div
                      className="alert alert-success"
                      style={{ fontSize: "16px", lineHeight: "1.8" }}
                    >
                      <h4 style={{ fontSize: "17px", marginBottom: "15px" }}>
                        الجرعة اليومية الموصى بها:
                      </h4>
                      <div
                        style={{
                          textAlign: "center",
                          padding: "20px",
                          backgroundColor: "#d4edda",
                          borderRadius: "8px",
                          marginBottom: "15px",
                        }}
                      >
                        <div
                          style={{ fontSize: "18px", marginBottom: "10px" }}
                        >
                          <strong>{vitaminName}</strong>
                        </div>
                        <div
                          style={{
                            fontSize: "32px",
                            fontWeight: "bold",
                            color: "#155724",
                          }}
                        >
                          {dose} {vitaminUnit}
                        </div>
                      </div>
                      <div style={{ fontSize: "14px", marginTop: "15px" }}>
                        <strong>ملاحظة:</strong>
                        <ul style={{ marginTop: "10px", lineHeight: "1.6" }}>
                          <li>
                            هذه الجرعة تمثل الكمية اليومية الموصى بها (RDA)
                          </li>
                          <li>
                            احتياجاتك الفعلية قد تختلف حسب حالتك الصحية ونظامك
                            الغذائي
                          </li>
                          <li>استشر طبيبك قبل تناول أي مكملات غذائية</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <hr />
            <div
              className="alert alert-info"
              style={{ fontSize: "15px", lineHeight: "1.8" }}
            >
              يمكنك أيضاً استخدام{" "}
              <a href="/calculator/ar/vitamin_daily_dose/">
                حاسبة الجرعة اليومية للفيتامينات
              </a>{" "}
              لحساب الجرعة اليومية للفيتامينات المختلفة.
            </div>
            <hr />
            <div className="row">
              <RelatedLinks />
            </div>
          </div>
        </div>
        <div className="col-sm-12 col-md-4">
          <SecondAds />
          <br />
          <MainCalculatorsList />
        </div>
      </div>
      {/* Educational Content */}
      <div className="row" style={{ marginTop: "20px" }}>
        <div className="col-12 col-md-8">
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header">
                <strong>أهمية {vitaminName}</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  {currentVitaminInfo.importance}
                </p>
                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>
                  لماذا تحتاج {vitaminName}؟
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>دعم وظائف الجسم الأساسية والحيوية</li>
                  <li>تعزيز الصحة العامة والوقاية من الأمراض</li>
                  <li>ضمان النمو والتطور الطبيعي</li>
                  <li>تحسين نوعية الحياة والطاقة اليومية</li>
                </ul>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <strong>مصادر {vitaminName} الطبيعية</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  أفضل طريقة للحصول على {vitaminName} هي من خلال{" "}
                  <strong>نظام غذائي متوازن</strong>. إليك أهم المصادر
                  الطبيعية:
                </p>
                <div
                  className="alert alert-success"
                  style={{ lineHeight: "1.8", backgroundColor: "#d4edda" }}
                >
                  <h4 style={{ fontSize: "16px", marginBottom: "10px" }}>
                    <strong>مصادر غذائية غنية ب{vitaminName}:</strong>
                  </h4>
                  <p>{currentVitaminInfo.sources}</p>
                </div>
                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>
                  نصائح للحصول على {vitaminName}:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>تناول مجموعة متنوعة من الأطعمة الطازجة يومياً</li>
                  <li>حافظ على نظام غذائي متوازن يشمل جميع المجموعات الغذائية</li>
                  <li>تجنب الطهي الزائد الذي قد يقلل من قيمة الفيتامينات</li>
                  <li>
                    استشر طبيبك إذا كنت تعتقد أنك بحاجة لمكملات غذائية
                  </li>
                </ul>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <strong>أعراض نقص {vitaminName}</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  نقص {vitaminName} يمكن أن يؤدي إلى مشاكل صحية متعددة. من
                  المهم التعرف على الأعراض مبكراً:
                </p>
                <div
                  className="alert alert-warning"
                  style={{ lineHeight: "1.8", backgroundColor: "#fff3cd" }}
                >
                  <h4 style={{ fontSize: "16px", marginBottom: "10px" }}>
                    <strong>علامات وأعراض النقص:</strong>
                  </h4>
                  <p>{currentVitaminInfo.deficiency}</p>
                </div>
                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>
                  الوقاية من النقص:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>اتبع نظاماً غذائياً متوازناً وغنياً بالفيتامينات</li>
                  <li>استخدم حاسبة الفيتامينات لمعرفة احتياجك اليومي</li>
                  <li>أجرِ فحوصات دورية لمستويات الفيتامينات</li>
                  <li>استشر طبيبك إذا ظهرت عليك أعراض النقص</li>
                </ul>
                <div
                  className="alert alert-danger mt-3"
                  style={{ lineHeight: "1.8", backgroundColor: "#f8d7da" }}
                >
                  <strong>⚠️ متى تزور الطبيب؟</strong>
                  <ul style={{ marginTop: "10px" }}>
                    <li>إذا كنت تعاني من أعراض مستمرة لنقص الفيتامينات</li>
                    <li>قبل تناول أي مكملات غذائية بجرعات عالية</li>
                    <li>إذا كنت حاملاً أو تخططين للحمل</li>
                    <li>إذا كنت تتبع نظاماً غذائياً مقيداً</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
         </div>
</div>
  );
}
export default VitaminDoseCalculator;